import type { ContaConectada, ConviteRecebido } from "../types";

export type FiltroDeConta = "TODAS" | "RECONECTAR" | "COM_CONVITES" | "SEM_ACESSO";

export const FILTROS_DE_CONTA: { chave: FiltroDeConta; rotulo: string }[] = [
  { chave: "TODAS", rotulo: "Todas" },
  { chave: "RECONECTAR", rotulo: "Precisam reconectar" },
  { chave: "COM_CONVITES", rotulo: "Com convites pendentes" },
  { chave: "SEM_ACESSO", rotulo: "Com perfis sem acesso" },
];

export const problemasDaConta = (conta: ContaConectada): number =>
  conta.semAcesso + conta.suspensos + conta.copias;

/** Os quatro números do topo: o que a equipe precisa olhar primeiro. */
export const resumoDasContas = (contas: ContaConectada[]) => ({
  conectadas: contas.filter((conta) => conta.connected).length,
  reconectar: contas.filter((conta) => conta.hasToken && conta.needsReauthentication).length,
  semToken: contas.filter((conta) => !conta.hasToken).length,
  convites: contas.reduce((total, conta) => total + conta.invitationsPending, 0),
  perfis: contas.reduce((total, conta) => total + conta.profiles, 0),
  comProblema: contas.reduce((total, conta) => total + problemasDaConta(conta), 0),
});

const aceita = (conta: ContaConectada, filtro: FiltroDeConta): boolean => {
  switch (filtro) {
    case "RECONECTAR":
      return !conta.connected;
    case "COM_CONVITES":
      return conta.invitationsPending > 0;
    case "SEM_ACESSO":
      return conta.semAcesso > 0;
    default:
      return true;
  }
};

export const filtrarContas = (
  contas: ContaConectada[],
  filtro: FiltroDeConta,
  busca: string,
): ContaConectada[] => {
  const termo = busca.trim().toLowerCase();
  return contas.filter(
    (conta) =>
      aceita(conta, filtro) &&
      (!termo ||
        (conta.email ?? "").toLowerCase().includes(termo) ||
        (conta.name ?? "").toLowerCase().includes(termo)),
  );
};

/** Situação da conexão em palavras de quem opera, não em flags. */
export const situacaoDaConta = (
  conta: ContaConectada,
): { rotulo: string; cor: "verde" | "vermelha" | "cinza" } => {
  if (conta.connected) return { rotulo: "Conectada", cor: "verde" };
  if (conta.hasToken && conta.needsReauthentication) return { rotulo: "Precisa reconectar", cor: "vermelha" };
  return { rotulo: "Sem conexão com o Google", cor: "cinza" };
};

const PAPEIS: Record<string, string> = {
  OWNER: "proprietário",
  PRIMARY_OWNER: "proprietário principal",
  CO_OWNER: "coproprietário",
  MANAGER: "gerente",
  SITE_MANAGER: "gerente do site",
};

export const descricaoDoConvite = (convite: ConviteRecebido): string => {
  const alvo = convite.targetName?.trim() || "uma ficha";
  const papel = convite.role ? (PAPEIS[convite.role] ?? convite.role.toLowerCase()) : null;
  const tipo = convite.targetType === "ACCOUNT" ? "a conta" : "a ficha";
  return `Convite para gerenciar ${tipo} “${alvo}”${papel ? ` como ${papel}` : ""}`;
};

export const iniciais = (texto: string | null | undefined, reserva: string): string => {
  const base = (texto ?? "").trim() || reserva;
  const partes = base.replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
  return partes
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? "")
    .join("") || "?";
};
